import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject, Input, OnChanges, OnDestroy, OnInit, Provider, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  FormGroupComponent,
  FormsUtil,
  GuidEmpty,
  InputButton,
  InputComponent,
  LoaderComponent,
  ModalService,
  ModalSettings,
  ToastService,
  TreeComponent,
  TreeElementDirective
} from 'mbs-ui-kit';
import {
  SearchModalRestoreResult,
  SearchResultModalComponent
} from 'mbs-ui-shared@components/sidepanel-backup/modals/search-result-modal/search-result-modal.component';
import { BackupStorageService } from 'mbs-ui-shared@components/sidepanel-backup/tabs/backup-storage/service/backup-storage.service';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import {
  AgentVersionForSearch,
  ItemTypeEnum,
  MyTreeElements,
  SearchButtonsId,
  TreeRestorePointsIconPath
} from 'mbs-ui-shared@models/backup/storages-type';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { noop } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RestoreSourceStepFormValue, RestoreSourceStepValue } from 'wizards/models/restore-source-models';
import { TreeIconPath } from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { GetBackupContentParams, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { CheckboxLegendComponent } from 'wizards/steps/components/checkbox-legend/checkbox-legend.component';
import { FilesFoldersListComponent } from 'wizards/steps/components/files-folders-list/files-folders-list.component';
import { BaseForRestoreSourceOrDisksStep } from 'wizards/steps/only-restore-steps/base/base-for-restore-source-or-disks-step.directive';
import { FormPipeOperators } from 'wizards/steps/StepBase.class';

const RestoreSourceStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => RestoreSourceStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-restore-source-step',
  templateUrl: './restore-source-step.component.html',
  styleUrls: ['./restore-source-step.component.scss'],
  providers: [RestoreSourceStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CardComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    LoaderComponent,
    ReactiveFormsModule,
    TreeComponent,
    FilesFoldersListComponent,
    HelpLinkDirective,
    TreeElementDirective,
    InputComponent,
    FormsModule
  ]
})
export class RestoreSourceStepComponent
  extends BaseForRestoreSourceOrDisksStep<RestoreSourceStepValue>
  implements OnInit, OnChanges, OnDestroy
{
  i18nextService = inject(I18NextService);
  mainService: RemoteManagementWizardsService;
  modal: ModalService;
  stepService: WizardStepsService;
  toastService: ToastService;
  private auth = inject(AuthFacade);
  private backupStorageService = inject(BackupStorageService);

  @Input() mainLoading: boolean;

  @ViewChild('fileFoldersComponent', { static: true, read: FilesFoldersListComponent }) fileFoldersComponent: FilesFoldersListComponent;

  public readonly searchButtonsId = SearchButtonsId;

  // Search section start
  public agentCanSearch = false;
  public searchLoading = false;
  public searchString = '';
  // Search section end

  public minusPixels = 50;

  public foldersForm = new UntypedFormGroup({ filesFoldersData: new FormControl({ enabledFields: true, paths: [] }) });

  public notSelectedPoint = false;

  public restoreSourceInfoAlert = this.i18nextService.t('wizards:restore_source_info_alert', { returnObjects: true });

  private firstLoad = true;

  private currentGeneration: MyTreeElements;

  get existSearchResult(): boolean {
    return !!this.backupStorageService?.searchResult()?.length;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);
    const modal = inject(ModalService);
    const stepService = inject(WizardStepsService);
    const toastService = inject(ToastService);

    super();

    this.mainService = mainService;
    this.modal = modal;
    this.stepService = stepService;
    this.toastService = toastService;

    this.calcMinusPixelsForCard();
  }

  ngOnInit(): void {
    this.auth.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.allowedRecovery = user.ProviderInfo.PasswordRecoveryEnabled && user.IsProvider;
    });

    this.initForm();

    this.setValues();
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this), debounceTime(200)];
  }

  initForm(): void {
    this.stepForm = new FormGroup<RestoreSourceStepFormValue>({
      folders: new FormControl([], [Validators.required]),
      point: new FormControl('')
    });

    this.subscribeToFoldersForm();
    this.initFormEvents();
  }

  subscribeToFoldersForm(): void {
    this.foldersForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.filesFoldersData && value.filesFoldersData.paths && value.filesFoldersData.paths.length) {
        this.stepForm.get('folders').setValue(value.filesFoldersData.paths);
      }
    });
  }

  onStepFormChange(value: RestoreSourceStepValue): void {
    if (this.isVMRestore) this.notSelectedPoint = !this.isNBF && this.manually && value.folders?.length && !value.point;

    this.value = { ...value, valid: this.stepForm.valid && !this.notSelectedPoint };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bunchId?.currentValue && changes.bunchId.currentValue !== changes.bunchId.previousValue) {
      this.mainService.password.next(this.passwordsForBunches[changes.bunchId.currentValue] || '');
      setTimeout(() => this.resetCanSearch());
    }

    if (
      this.isRDMode ||
      (changes.computerName && !changes.computerName.currentValue) ||
      (changes.bunchId && !changes.bunchId.currentValue) ||
      (changes.storageId && (!changes.storageId.currentValue || changes.storageId.currentValue === GuidEmpty))
    ) {
      this.treeData = [];
    }

    if (!this.isRDMode && this.computerName && this.storageId && this.storageId !== GuidEmpty) {
      const bunchIsChanged = !this.isNBF && changes.bunchId?.currentValue && changes.bunchId.currentValue !== GuidEmpty;
      const manuallyChanged = changes.manually && this.bunchId;
      const pointChanged =
        changes.resultPoint?.currentValue &&
        (!this.isNBF || !changes.resultPoint.currentValue.needDeepSync || changes.resultPoint.currentValue.needRunSync) &&
        this.bunchId;

      if (bunchIsChanged || manuallyChanged || pointChanged) {
        this.resetComputersIfCreateOrNotFirstLoad();
        this.loadTreeData();
      } else if (
        this.treeData?.length &&
        this.stepFocused &&
        !this.isRestoreDB &&
        (!this.isVMRestore || (!this.isNBF && this.manually)) &&
        this.stepForm?.get('folders')?.value.length
      ) {
        this.treeData = this.treeData.map((item) => ({ ...item, children: null, gotChildren: false }));
        this.updateTreeFromStepFormArray();
      }
    }
  }

  searchClickHandler(data?: InputButton): void {
    if (!this.searchString && data?.id !== SearchButtonsId.edit) return;

    this.searchLoading = true;

    const pathArray = this.resultPoint.path.split(this.joinForInput);
    const generationId = pathArray[pathArray.length - 2];

    this.stepService
      .getRemoteCommandData(this.getParamsForGenerations(pathArray), this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (generationsData) => {
          if (!generationsData?.data?.items) return void (this.searchLoading = false);

          const found = generationsData.data.items.find((el) => el.id === generationId);
          this.currentGeneration = { ...(found || {}), displayName: found.displayName, label: found.displayName };
          this.openSearchModal(data, pathArray);
          this.searchLoading = false;
        },
        error: () => (this.searchLoading = false)
      });
  }

  resetComputersIfCreateOrNotFirstLoad(): void {
    if ((this.isCreate || !this.firstLoad) && !this.mainService.initialFormData?.restoreSourceStep) {
      if (this.stepForm) {
        this.stepForm.get('folders').markAsTouched();
        this.stepForm.get('folders').setValue([]);
      }

      return;
    }

    if (!this.isUnix || this.isNBF) return void (this.firstLoad = false);

    if (this.resultPoint) this.firstLoad = false;
  }

  updateForm(value: RestoreSourceStepValue): void {
    this.stepForm.reset(value);
  }

  forceValid(): void {
    if (this.isRDMode) this.fileFoldersComponent.updateValidate();
    FormsUtil.triggerValidation(this.stepForm);
  }

  showLegend(): void {
    const modal = this.modal.openRef(CheckboxLegendComponent, { isCustom: true });

    modal.componentInstance.isRestore = true;

    modal.result.then(noop).catch(noop);
  }

  ngOnDestroy(): void {
    this.stepService.treeData$?.next(null);
  }

  private openSearchModal(data?: InputButton, pathArray?: string[]): void {
    this.modal
      .openCustom(SearchResultModalComponent, this.getSearchModalSettings(data?.id === SearchButtonsId.edit, pathArray))
      .then((result: SearchModalRestoreResult) => {
        if (!result?.selectedItems?.length) return;

        this.updateFormFromSearchModal(result.selectedItems);
      })
      .catch();
  }

  private updateFormFromSearchModal(selectedItems: MyTreeElements[]): void {
    let selected = this.stepForm.get('folders').value;
    let newSelected = [];

    selectedItems.forEach((item) => {
      const path = StepsHelpers.getPathFromItem(item, this.joinForInput, true, this.isUnix);

      newSelected.push(path);
      selected = selected.filter(
        (s) =>
          !StepsHelpers.equalStrings(s, path, this.isUnix) &&
          StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, path, s) &&
          StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, path)
      );
    });

    [...newSelected].forEach(
      (path) =>
        (newSelected = newSelected.filter(
          (s) => StepsHelpers.equalStrings(s, path, this.isUnix) || StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, s, path)
        ))
    );

    this.setNewPathArray([...selected, ...newSelected]);
    this.updateTreeFromStepFormArray();
    this.treeData.map((e) => {
      e.expanded = false;
      e.gotChildren = false;
      e.children = [];
    });
    this.treeData = cloneDeep(this.treeData);
  }

  private getParamsForGenerations(pathArray: string[]): GetBackupContentParams {
    return {
      agentType: 'backup',
      commandType: 'GetBackupContent',
      params: {
        ConnectionId: '',
        RestoreSourcePrefix: '',
        BunchId: '',
        RestorePointDateUtc: '',
        path: pathArray.slice(0, pathArray.length - 2).join(this.joinForInput),
        offset: 0,
        limit: 300,
        order: 'DisplayNameAsc'
      }
    };
  }

  private getSearchModalSettings(useHistory = false, pathArray: string[]): ModalSettings {
    const baseIdList = { prefix: this.computerName, storageId: this.storageId, bunchId: this.bunchId };
    const bunch = {
      ...baseIdList,
      iconCustom: TreeIconPath.Folder,
      path: pathArray.slice(0, pathArray.length - 2).join(this.joinForInput),
      label: this.stepService.backupPlanName,
      itemType: ItemTypeEnum.Bunch
    };
    const generation = {
      ...this.currentGeneration,
      ...baseIdList,
      label: this.currentGeneration.label || this.currentGeneration.firstRestoreDate + '-' + this.currentGeneration.lastRestoreDate,
      iconCustom: TreeRestorePointsIconPath.Chain,
      itemType: ItemTypeEnum.Generation
    };
    const point = {
      ...this.resultPoint,
      ...baseIdList,
      label: this.resultPoint.displayName || this.resultPoint.date,
      iconCustom: this.resultPoint.isFull
        ? TreeRestorePointsIconPath.Full
        : this.resultPoint.itemType === ItemTypeEnum.RestorePointTLog
          ? TreeRestorePointsIconPath.File
          : TreeRestorePointsIconPath.Diff,
      generationId: generation.generationId
    };

    const resultObject = {
      ...point,
      parent: { ...generation, children: [point], parent: { ...bunch, children: [generation], parent: {} } }
    };

    return {
      size: this.MbsSize.xxl,
      collapsing: true,
      responsive: true,
      data: {
        treeElement: resultObject,
        searchStr: this.searchString,
        showFileVersions: false,
        useHistory: useHistory,
        computer: { hid: this.mainService.hid, apps: [{ applicationId: AgentType.Backup, version: this.mainService.backupVersion }] },
        force: true
      }
    };
  }

  private resetCanSearch(): void {
    if (this.isNBF && this.isRestorePlan && !this.isOffline && !this.isRDMode) {
      const version = this.mainService.backupVersionUpdated && this.mainService.backupVersionUpdated.substring(0, 3);
      this.agentCanSearch = version && +version >= AgentVersionForSearch;
      this.minusPixels = 50;
      this.calcMinusPixelsForCard();
    }
  }

  private calcMinusPixelsForCard(): void {
    if (!this.isVMRestore && !this.isRestoreDB) this.minusPixels += 185;
    if (this.isVMRestore || this.isRestoreDB) this.minusPixels += 145;
    if (this.notSelectedPoint) this.minusPixels += 50;
    if (this.agentCanSearch) this.minusPixels += 35;
    if (this.isOffline) this.minusPixels += 35;
  }
}
